import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, X, SlidersHorizontal } from "lucide-react";
import Fuse from "fuse.js";
import { useRealisations, resolveImage, useTaxonomies } from "../lib/realisations";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { useContent } from "../lib/content";
import { heroCardStyle } from "../lib/heroCard";

const PER_PAGE = 12;

const missionColor = (_key) => "bg-[#E60000] text-white";

const FUSE_OPTIONS = {
  // threshold 0.4 = tolère ~40% de différences (assez de fautes de frappe)
  threshold: 0.4,
  ignoreLocation: true,
  includeScore: true,
  minMatchCharLength: 2,
  keys: [
    { name: "name",        weight: 0.35 },
    { name: "location",    weight: 0.20 },
    { name: "client",      weight: 0.10 },
    { name: "architect",   weight: 0.08 },
    { name: "usage",       weight: 0.07 },
    { name: "type",        weight: 0.06 },
    { name: "year",        weight: 0.05 },
    { name: "category",    weight: 0.03 },
    { name: "description", weight: 0.03 },
    { name: "missions",    weight: 0.02 },
    { name: "extra_fields.value", weight: 0.01 },
    { name: "extra_fields.label", weight: 0.01 },
  ],
};

export default function Realisations() {
  const { hash } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { list: REALISATIONS, loading } = useRealisations();
  const { categories: CATEGORIES, missions: MISSIONS } = useTaxonomies();
  const cContent = useContent("realisations");
  const c = cContent;
  // Init from URL: ?filter=opc,logements&q=...
  const [active, setActive] = useState(() => {
    const f = (searchParams.get("filter") || "").split(",").map((s) => s.trim()).filter(Boolean);
    return new Set(f);
  });
  const [query, setQuery] = useState(() => searchParams.get("q") || "");
  const [page, setPage] = useState(() => Math.max(1, parseInt(searchParams.get("p") || "1", 10)));
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const searchWrapRef = useRef(null);

  // Keep URL in sync (so the link is shareable)
  useEffect(() => {
    const next = {};
    if (active.size > 0) next.filter = Array.from(active).join(",");
    if (query.trim()) next.q = query.trim();
    if (page > 1) next.p = String(page);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, query, page]);

  const toggleFilter = (key) => {
    setActive((prev) => {
      const next = new Set(prev);
      if (key === "all") { return new Set(); }
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const clearFilters = () => setActive(new Set());
  const isActive = (key) => (key === "all" ? active.size === 0 : active.has(key));

  const FILTERS = useMemo(() => [
    { key: "all", label: "Tous", type: "all" },
    { key: "featured", label: "Mises en avant", type: "featured" },
    ...MISSIONS.map((m) => ({ ...m, type: "mission" })),
    ...CATEGORIES.map((c) => ({ ...c, type: "category" })),
  ], [MISSIONS, CATEGORIES]);

  const fuse = useMemo(() => {
    if (!REALISATIONS?.length) return null;
    return new Fuse(REALISATIONS, FUSE_OPTIONS);
  }, [REALISATIONS]);

  // Apply each selected filter as OR within same group, AND between groups.
  // Behaviour:
  //   - aucune sélection ⇒ tout
  //   - missions sélectionnées ⇒ projet doit avoir AU MOINS UNE mission cochée
  //   - catégories sélectionnées ⇒ projet doit appartenir à UNE des catégories cochées
  //   - "featured" ⇒ projet doit être en vedette
  //   - les groupes (featured, missions, catégories) sont combinés en AND
  const filteredByFilter = useMemo(() => {
    if (!REALISATIONS) return [];
    if (active.size === 0) return REALISATIONS;
    const selectedMissions = [];
    const selectedCategories = [];
    let featuredOnly = false;
    for (const k of active) {
      if (k === "featured") featuredOnly = true;
      else if (MISSIONS.some((m) => m.key === k)) selectedMissions.push(k);
      else if (CATEGORIES.some((c) => c.key === k)) selectedCategories.push(k);
    }
    return REALISATIONS.filter((r) => {
      if (featuredOnly && !r.featured) return false;
      if (selectedMissions.length && !selectedMissions.some((m) => (r.missions || []).includes(m))) return false;
      if (selectedCategories.length && !selectedCategories.includes(r.category)) return false;
      return true;
    });
  }, [active, REALISATIONS, MISSIONS, CATEGORIES]);

  const filtered = useMemo(() => {
    if (!query.trim()) return filteredByFilter;
    if (!fuse) return filteredByFilter;
    const hits = fuse.search(query.trim()).map((r) => r.item);
    if (active.size === 0) return hits;
    const allowedIds = new Set(filteredByFilter.map((r) => r.id));
    return hits.filter((r) => allowedIds.has(r.id));
  }, [query, fuse, filteredByFilter, active]);

  // Suggestions = top 6 fuzzy matches for the dropdown
  const suggestions = useMemo(() => {
    if (!query.trim() || !fuse) return [];
    return fuse.search(query.trim(), { limit: 6 }).map((r) => r.item);
  }, [query, fuse]);

  // Close suggestions on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => { setPage(1); }, [active, query]);

  // Legacy hash redirect: /realisations#slug → /realisations/slug
  useEffect(() => {
    if (hash && REALISATIONS) {
      const id = hash.slice(1);
      const proj = REALISATIONS.find((r) => r.slug === id || r.id === id);
      if (proj) window.location.replace(`/realisations/${proj.slug}`);
    }
  }, [hash, REALISATIONS]);

  const categoryLabel = (key) => CATEGORIES.find((c) => c.key === key)?.label || key;

  return (
    <div data-testid="page-realisations">
      <SEO
        path="/realisations"
        title="Réalisations — Logements, tertiaire, scolaire | ECO-GEST PATRIMOINE"
        description="Découvrez nos opérations en images : logements collectifs, tertiaire, équipements publics et scolaires. Plus de 50 projets accompagnés en Haute-Savoie et Auvergne-Rhône-Alpes."
      />
      {/* HERO */}
      <section className="relative h-[50vh] min-h-[360px] bg-[#1A1A1A] text-white overflow-hidden flex items-end">
        <img src={resolveImage(c.hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: ((c.hero_image_opacity ?? 100) / 100) }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pb-14 w-full">
          <div
            className="inline-block border-l-2 border-[#E60000] rounded-r-lg px-6 py-5 shadow-xl shadow-black/15"
            style={heroCardStyle(c)}
          >
            <p className="inline-flex items-center gap-3 text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-white">
              <span className="inline-block w-8 h-px bg-[#E60000]" />
              {c.hero_breadcrumb}
            </p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-white mt-3">{c.hero_title}</h1>
          </div>
        </div>
      </section>

      {/* FILTERS — pill style + search */}
      <section className="sticky top-24 lg:top-28 z-30 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 space-y-3">
          <div ref={searchWrapRef} className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="search-realisations"
              type="search"
              autoComplete="off"
              value={query}
              onFocus={() => setShowSuggestions(true)}
              onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
              placeholder="Rechercher (ex : EHPAD ORPEA, Morzine, Genopole…)"
              className="w-full pl-10 pr-9 py-2.5 text-sm border border-gray-200 focus:border-[#E60000] focus:outline-none"
            />
            {query && (
              <button
                data-testid="clear-search"
                onClick={() => { setQuery(""); setShowSuggestions(false); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-[#E60000]"
                aria-label="Effacer"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Suggestions dropdown */}
            {showSuggestions && query.trim() && suggestions.length > 0 && (
              <div data-testid="search-suggestions" className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 shadow-lg z-50 max-h-96 overflow-y-auto">
                {suggestions.map((s) => (
                  <Link
                    key={s.id || s.slug}
                    to={`/realisations/${s.slug}`}
                    data-testid={`suggest-${s.slug}`}
                    onClick={() => setShowSuggestions(false)}
                    className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 flex-shrink-0 overflow-hidden bg-gray-100">
                      <img loading="lazy" src={resolveImage(s.cover)} alt="" className="img-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{s.name || "Sans titre"}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {s.location}
                        {s.year && ` · ${s.year}`}
                        {s.category && ` · ${categoryLabel(s.category)}`}
                      </p>
                    </div>
                  </Link>
                ))}
                <div className="px-3 py-2 text-[11px] text-gray-400 bg-gray-50 border-t border-gray-100">
                  Appuyez sur Entrée pour voir les {filtered.length} résultat{filtered.length > 1 ? "s" : ""} →
                </div>
              </div>
            )}
            {showSuggestions && query.trim() && suggestions.length === 0 && (
              <div data-testid="search-no-suggestions" className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 shadow-lg z-50 p-4 text-sm text-gray-500">
                Aucune suggestion. Essayez « EHPAD », « logements », « Annecy »…
              </div>
            )}
          </div>
          {/* Filters — pills on desktop, dropdown drawer on mobile */}
          <div className="flex items-center gap-3">
            {/* Mobile : bouton qui ouvre le drawer */}
            <button
              data-testid="open-filters-mobile"
              onClick={() => setFiltersOpen((v) => !v)}
              className="md:hidden inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase font-semibold px-4 py-2 border border-gray-300 hover:border-[#E60000] hover:text-[#E60000]"
              aria-expanded={filtersOpen}
              aria-controls="mobile-filters-panel"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtres
              {active.size > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-[#E60000] text-white rounded-full">{active.size}</span>
              )}
            </button>

            {/* Desktop : pills visibles */}
            <div className="hidden md:flex flex-wrap gap-2 items-center overflow-x-auto">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`filter-${f.key}`}
                  onClick={() => (f.key === "all" ? clearFilters() : toggleFilter(f.key))}
                  className={`text-xs tracking-[0.12em] uppercase font-semibold px-4 py-2 transition-colors whitespace-nowrap border ${
                    isActive(f.key)
                      ? "bg-[#E60000] text-white border-[#E60000]"
                      : "bg-white text-gray-700 border-gray-200 hover:border-[#E60000] hover:text-[#E60000]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
              {active.size > 0 && (
                <button
                  data-testid="clear-filters"
                  onClick={clearFilters}
                  className="text-xs tracking-[0.12em] uppercase font-semibold px-3 py-2 text-gray-500 hover:text-[#E60000] underline-offset-2 hover:underline"
                >
                  Effacer ({active.size})
                </button>
              )}
            </div>

            <span data-testid="results-count" className="ml-auto text-xs text-gray-500 whitespace-nowrap pl-2">
              {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
            </span>
          </div>

          {/* Mobile drawer */}
          {filtersOpen && (
            <div
              id="mobile-filters-panel"
              data-testid="mobile-filters-panel"
              className="md:hidden bg-white border border-gray-200 p-3 shadow-sm"
            >
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    data-testid={`mfilter-${f.key}`}
                    onClick={() => (f.key === "all" ? clearFilters() : toggleFilter(f.key))}
                    className={`text-[11px] tracking-[0.1em] uppercase font-semibold px-3 py-1.5 transition-colors whitespace-nowrap border ${
                      isActive(f.key)
                        ? "bg-[#E60000] text-white border-[#E60000]"
                        : "bg-white text-gray-700 border-gray-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {active.size > 0 && (
                <div className="mt-3 flex items-center justify-between text-[11px]">
                  <span className="text-gray-500">{active.size} filtre{active.size > 1 ? "s" : ""} actif{active.size > 1 ? "s" : ""}</span>
                  <button
                    data-testid="mclear-filters"
                    onClick={clearFilters}
                    className="uppercase tracking-wider font-semibold text-[#E60000] hover:underline"
                  >
                    Tout effacer
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <p className="text-center text-gray-500 py-20">Chargement…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-20">Aucune réalisation ne correspond à votre recherche.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pageItems.map((p, i) => (
              <Reveal delay={i * 100} key={p.id || p.slug}>
                  <Link
                    to={`/realisations/${p.slug}`}
                    data-testid={`project-card-${p.slug || p.id}`}
                    className="group text-left w-full bg-white border border-gray-200 hover:border-[#E60000] transition-colors duration-500 flex flex-col h-full"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-gray-100 relative">
                      <img loading="lazy" src={resolveImage(p.cover)} alt={p.name} className="img-cover group-hover:scale-[1.05] transition-transform duration-[1200ms]" />
                      {p.year && (
                        <span className="absolute top-3 right-3 bg-white/95 text-gray-900 text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1">
                          {p.year}
                        </span>
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-display text-lg font-bold tracking-tight mb-2">{p.name || "Sans titre"}</h3>
                      {p.category && (
                        <div className="mb-3">
                          <span className={`inline-block text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 ${missionColor(p.category)}`}>
                            {categoryLabel(p.category)}
                          </span>
                        </div>
                      )}
                      {(p.missions || []).length > 0 && (
                        <p className="text-xs text-gray-500 mb-1">
                          <span className="text-gray-700 font-medium">Mission : </span>
                          {p.missions.map((m) => MISSIONS.find((x) => x.key === m)?.label || m).join(" · ")}
                        </p>
                      )}
                      {p.type && <p className="text-xs text-gray-500">{p.type}</p>}
                      {(p.budget || p.surface) && (
                        <p className="text-xs text-gray-600 mt-1 font-medium">
                          {p.budget && <span>{p.budget}</span>}
                          {p.budget && p.surface && <span className="text-gray-400"> · </span>}
                          {p.surface && <span>{p.surface}</span>}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-auto pt-3">{p.location}</p>
                      {p.usage && (
                        <p className="text-[11px] text-gray-500 italic mt-1">Usage&nbsp;: {p.usage}</p>
                      )}
                      {p.architect && (
                        <p className="text-[11px] text-gray-500 italic mt-1">Architecte&nbsp;: {p.architect}</p>
                      )}
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}

      {/* PAGINATION */}
          {totalPages > 1 && (
            <div data-testid="pagination" className="mt-16 flex items-center justify-center gap-2">
              <button
                data-testid="pagination-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-10 h-10 flex items-center justify-center border border-gray-200 disabled:opacity-40 hover:border-[#E60000] transition-colors"
                aria-label="Page précédente"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
              </button>
              {Array.from({ length: totalPages }).map((_, i) => {
                const n = i + 1;
                return (
                  <button
                    key={n}
                    data-testid={`pagination-page-${n}`}
                    onClick={() => setPage(n)}
                    className={`w-10 h-10 flex items-center justify-center text-sm font-semibold border transition-colors ${
                      page === n
                        ? "bg-[#E60000] text-white border-[#E60000]"
                        : "bg-white text-gray-700 border-gray-200 hover:border-[#E60000] hover:text-[#E60000]"
                    }`}
                  >
                    {n}
                  </button>
                );
              })}
              <button
                data-testid="pagination-next"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-10 h-10 flex items-center justify-center border border-gray-200 disabled:opacity-40 hover:border-[#E60000] transition-colors"
                aria-label="Page suivante"
              >
                <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>
      </section>

    </div>
  );
}
